// A product's name in parts. An item ("Lemons") is sold by one or more units ("case",
// "each", "2 lb bag"); each unit is its own product, and its official name is the item
// and the unit joined: "Lemons - case", "Lemons - each". `products.name` is that name
// and stays the one official name: pick sheets, invoices, order lines and the QuickBooks
// item all print it. The item is the umbrella the menu card and the product form are
// headed by — never an order line of its own (Jon, 2026-10-14). Anything that tells two
// versions of an item apart ("Navel Oranges 48s") belongs in the item.
//
// `pack` is the middle part an earlier form (#284, 10-13) stored: old rows keep it, and
// it still sits between the item and the unit in the name (and is the row's tag on the
// menu), until the product form clears it. No new product is given one. Shared by the product routes (server) and the menu and product
// form (client). Pure functions only.
import { PRODUCT_NAME_SEPARATOR, sizeUnitSuffix, type SizeGroup } from "./productSizes";

export const ITEM_NAME_MAX = 120;
export const PACK_MAX = 60;

type Named = { name: string; itemName?: string | null; pack?: string | null };

const sameText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "accent" }) === 0;

/** One line of typed text as it is stored: trimmed, runs of spaces closed up. */
export function tidyNamePart(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/**
 * A product's item and pack. A product with no item stored (written by name alone: an
 * import, a QuickBooks pull, anything saved before the two boxes) goes by its whole
 * name, with no pack — which is how it read on the menu before.
 */
export function productParts(product: Named): { item: string; pack: string } {
  const item = product.itemName?.trim();
  return item ? { item, pack: product.pack?.trim() ?? "" } : { item: product.name, pack: "" };
}

/**
 * The product's name with its unit left off, for a place that prints the unit in a
 * column of its own (the pick sheet with its Unit column on, Jon 2026-10-16): the item,
 * then an old row's pack — "Oranges - 3lb bag" [3lb bag] → "Oranges", "Oranges - 9x3lb
 * bag - case" [case] → "Oranges - 9x3lb bag". A product with no item stored goes by its
 * whole name, less a trailing " - <unit>" when it ends in one. Display only: the official
 * name (`products.name`) is never changed by this.
 */
export function productNameWithoutUnit(product: Named & { unit?: string | null }): string {
  if (product.itemName?.trim()) {
    const { item, pack } = productParts(product);
    return pack ? `${item}${PRODUCT_NAME_SEPARATOR}${pack}` : item;
  }
  const unit = product.unit?.trim();
  if (unit) {
    const endings = sizeUnitSuffix(unit) === unit ? [unit] : [unit, sizeUnitSuffix(unit)];
    for (const ending of endings) {
      const suffix = `${PRODUCT_NAME_SEPARATOR}${ending}`;
      if (product.name.length > suffix.length && sameText(product.name.slice(-suffix.length), suffix)) {
        return product.name.slice(0, -suffix.length);
      }
    }
  }
  return product.name;
}

export type NameInput = {
  item: string;
  /** An old row's stored pack (see the top of this file). "" = none. */
  pack?: string;
  unit?: string | null;
};

/**
 * The official name the parts make: the item, then an old row's pack, then the unit.
 *
 *   "Lemons" [case]                     → "Lemons - case"
 *   "Navel Oranges 48s" [each]          → "Navel Oranges 48s - each"
 *   "Black Plums" [lb]                  → "Black Plums - per lb"
 *   "Lemons" with no unit               → "Lemons"
 *   "Oranges" / "9x3lb bag" [case] (old) → "Oranges - 9x3lb bag - case"
 *
 * Product names are unique per org, and one item never has the same unit twice, so
 * every unit of an item gets a name of its own.
 */
export function composeProductName({ item, pack, unit }: NameInput): string {
  return [item, pack ?? "", unit ? sizeUnitSuffix(unit) : ""].filter((part) => part.length > 0).join(PRODUCT_NAME_SEPARATOR);
}

/**
 * The name a save leaves the product with. Unchanged parts leave the stored name alone,
 * whatever it is — saving a price never renames a product, and a name written before
 * the item-and-unit rule (or by hand) is kept until its item or unit is actually edited.
 */
export function nextProductName(stored: (Named & { unit?: string | null }) | null, next: NameInput): string {
  if (stored) {
    const was = productParts(stored);
    if (was.item === next.item && was.pack === (next.pack ?? "") && (stored.unit ?? null) === (next.unit ?? null)) {
      return stored.name;
    }
  }
  return composeProductName(next);
}

export type MenuCardLabels = {
  /** The card's heading: the item, and nothing else. */
  title: string;
  /**
   * One per member, in order: the tag its row reads (a single card's sits beside the
   * heading) — its unit, or an old row's pack in the unit's place. "" = neither.
   */
  tags: string[];
};

/**
 * How every card in one drawn list reads: one entry per group, in the groups' order.
 * A card is headed by its item, then each unit is a tag and a price — no text beside
 * the tag (Jon, 2026-10-14). A row that still has an old pack shows the pack as its tag
 * instead of the unit, until the pack is moved into the item (Jon, 2026-10-14):
 *
 *   "Lemons - case" + "Lemons - each"           → "Lemons"  [case] [each]
 *   "Carrots - 24x2lb" + "Carrots - 2lb bag"    → "Carrots" [24x2lb] [2lb bag]
 */
export function menuCardLabels<P extends Named & { unit?: string | null }>(groups: SizeGroup<P>[]): MenuCardLabels[] {
  return groups.map(({ head, members }) => ({
    title: productParts(head).item,
    tags: members.map((member) => productParts(member).pack || member.unit || ""),
  }));
}

/**
 * The item and pack a request body carries. `given` = the body named either; a body
 * that names neither is a name-only write and leaves this to the caller.
 */
export function readNameParts(body: { itemName?: unknown; pack?: unknown }): {
  given: boolean;
  item?: string;
  pack?: string;
  error?: string;
} {
  const { itemName, pack } = body;
  if (itemName === undefined && pack === undefined) return { given: false };
  if ((itemName !== undefined && typeof itemName !== "string") || (pack !== undefined && pack !== null && typeof pack !== "string")) {
    return { given: true, error: "The item and the pack are text." };
  }
  const item = itemName === undefined ? undefined : tidyNamePart(itemName as string);
  const tidyPack = pack === undefined ? undefined : tidyNamePart((pack as string | null) ?? "");
  if (item !== undefined && !item) return { given: true, error: "Enter the item's name." };
  if (item && item.length > ITEM_NAME_MAX) return { given: true, error: `Keep the item to ${ITEM_NAME_MAX} characters.` };
  if (tidyPack && tidyPack.length > PACK_MAX) return { given: true, error: `Keep the pack or size to ${PACK_MAX} characters.` };
  return { given: true, item, pack: tidyPack };
}
